import {WsGroup, WsSensor} from "../../../store/branches/sensors/stateTypes";

// Input 9/10 (IE-Node-1, deviceType "0005"): the same 2 physical wires as the "Pulse"
// (group 2000) and "CLI" (group 3000) sensors, DIP-switch selected — see
// hazardmon-udfconverter's CLAUDE.md "Sensor Settings" section, and
// hazardmon-frontend/src/components/EditSensorSettings/components/UnifiedInputForm.tsx
// for the admin-side settings page this mirrors.
//
// The converter always emits both a PPM numeric sensor (id 2001/2002) and a
// PS-derived Contact sibling (id 2011/2012, offset +10) for a Pulse-mode input,
// since it has no visibility into the admin's declared type — that decision is made
// here, client-side, against the same sensor-types settings the admin page uses.
export const PULSE_GROUP_ID = "2000";
export const CLI_GROUP_ID = "3000";
export const PULSE_STATE_ID_OFFSET = 10;

const INPUT_INDICES = [0, 1];

// Splits the raw "Pulse"/"CLI" groups into one synthetic group per *declared* type
// (Pulse/Contact/CLI), each carrying only the inputs that actually belong there, with
// a heading (Name) that matches — rather than one "Pulse:" section that might contain
// a Pulse-declared input and a Contact-declared input side by side. Feedback (2026-09-30
// testing): the previous approach (keep the "Pulse:"/"CLI:" heading, only relabel/hide
// individual sensor cards) was wrong — testers want the heading itself to say "Contact"
// when that's what's declared, and Input 9/10 on separate rows when they diverge.
//
// Returns `groups` unchanged for any device without both a Pulse and CLI group (i.e.
// anything other than IE-Node-1) — this function is a no-op there.
export const deriveInput910Groups = (
    groups: WsGroup[] | undefined,
    inputTypeBySensorId: Record<string, string> | undefined,
): WsGroup[] | undefined => {
    if (!groups) {
        return groups;
    }

    const pulseGroup = groups.find((g) => g.Id === PULSE_GROUP_ID);
    const cliGroup = groups.find((g) => g.Id === CLI_GROUP_ID);
    if (!pulseGroup && !cliGroup) {
        return groups;
    }

    const pulseSensors: WsSensor[] = [];
    const contactSensors: WsSensor[] = [];
    const cliSensors: WsSensor[] = [];

    INPUT_INDICES.forEach((i) => {
        const pulseId = String(2001 + i);
        const contactId = String(2001 + PULSE_STATE_ID_OFFSET + i);
        const cliId = String(3001 + i);
        const inputLabel = `Sensor ${9 + i}`;

        const pulseSensor = pulseGroup?.sensors?.find((s) => s.Id === pulseId);
        const contactSensor = pulseGroup?.sensors?.find((s) => s.Id === contactId);
        const cliSensor = cliGroup?.sensors?.find((s) => s.Id === cliId);

        if (pulseSensor) {
            // Hardware is DIP'd to Pulse for this input — whether it shows as Pulse or
            // Contact depends purely on Hazardmon's admin setting (stored on the CLI
            // sibling id regardless of hardware mode, see UnifiedInputForm.tsx).
            const wantsContact = inputTypeBySensorId?.[cliId] === "CONTACT";
            if (wantsContact && contactSensor) {
                contactSensors.push({...contactSensor, Name: inputLabel});
            } else {
                pulseSensors.push({...pulseSensor, Name: inputLabel});
            }
        } else if (cliSensor) {
            // Hardware DIP'd to CLI — Contact is never reachable here (no PS data),
            // regardless of what's declared.
            cliSensors.push({...cliSensor, Name: inputLabel});
        }
    });

    const synthesized: WsGroup[] = [];
    if (pulseSensors.length) {
        synthesized.push({
            Id: PULSE_GROUP_ID, // kept so icon lookups (keyed by group id) still resolve
            Name: "Pulse",
            Alarm: pulseSensors.some((s) => s.Alarm),
            Status: pulseGroup?.Status,
            groups: null,
            sensors: pulseSensors,
        });
    }
    if (contactSensors.length) {
        synthesized.push({
            Id: PULSE_GROUP_ID,
            Name: "Contact",
            Alarm: contactSensors.some((s) => s.Alarm),
            Status: pulseGroup?.Status,
            groups: null,
            sensors: contactSensors,
        });
    }
    if (cliSensors.length) {
        synthesized.push({
            Id: CLI_GROUP_ID,
            Name: "CLI",
            Alarm: cliSensors.some((s) => s.Alarm),
            Status: cliGroup?.Status,
            groups: null,
            sensors: cliSensors,
        });
    }

    // Splice the synthesized groups in at the position of the first Pulse/CLI group
    // encountered, dropping both originals — keeps NTC/AUX/etc. groups in their
    // original relative order.
    let inserted = false;
    const result: WsGroup[] = [];
    groups.forEach((g) => {
        if (g.Id === PULSE_GROUP_ID || g.Id === CLI_GROUP_ID) {
            if (!inserted) {
                result.push(...synthesized);
                inserted = true;
            }
            return;
        }
        result.push(g);
    });
    return result;
};
